import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { IRegister } from '@/components/sections/register';
import { clearBodyLockScroll } from '@/components/shared/utility/lock-scroll';
import clientContentStack from '@/services/contentstack/client';
import { IProvince } from '@/types/dealer/province';
import { ILCVModelType } from '@/types/services/contentstack';
import { IButton, IThemeLCV } from '@/types/shared';
import { IMy24Model } from '@/types/shared/my24/model';

const RegisterFormDialog = dynamic(
  () => import('@/components/shared/form/register-form-dialog'),
  {
    ssr: false,
  }
);

interface RegisterConfig {
  lcvModel: ILCVModelType;
  model: IMy24Model;
  remark?: string;
  dataTrackSection?: string;
  theme?: IThemeLCV;
}

interface RegisterApi {
  api: {
    open: (section?: string) => void;
    close: () => void;
    addButtonClick: (section: string, buttons: IButton[]) => void;
    options: IRegister;
  };
  component: React.ReactNode;
}

export const useRegisterDialog = (config: RegisterConfig) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [provinces, setProvinces] = useState<IProvince[]>([]);
  const [options, setOptions] = useState<IRegister>({
    theme: config?.theme ?? 'light',
    title: 'ลงทะเบียนสนใจรถ',
    campaignName: 'สนใจรถอีซูซุ / ทดลองขับ',
    campaignId: 'product-register',
    campaignVersion: '1.0.0',
    truck2hand: false,
    lcvModel: config?.lcvModel,
    provinces: provinces,
    remark: `${config?.remark ? config?.remark + ' ' : ''}${config?.lcvModel}|${
      config?.model
    }`,
    dataTrackSection: 'initial',
  });

  const addRegisterButtonsClick = (section: string, buttons: IButton[]) => {
    if (!buttons || buttons?.length === 0) return;
    const button = buttons.find((b) => b.label === 'สนใจรถ');
    if (!button) return;
    button.onClick = () => onOpen(section);
  };

  const onOpen = async (section?: string) => {
    if (section) {
      const inputProps = {
        ...options,
        dataTrackSection: section,
        remark: `${section}|${options?.remark}`,
      };
      setOptions({ ...inputProps });
    }
    setIsOpen(true);
  };

  const onSelfCloseModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const loadProvinces = async () => {
      try {
        const provinceRes = await clientContentStack({
          data: { route: 'getLCVProvince' },
        });
        setProvinces(provinceRes?.data?.entries);
      } catch (error) {
        setProvinces([]);
      }
    };

    // Fetch provinces only if it's not loaded yet.
    if (provinces?.length === 0) {
      loadProvinces();
    }
  }, [isOpen, provinces?.length]);

  useEffect(() => {
    if (!isOpen) {
      clearBodyLockScroll();
    }
  }, [isOpen]);

  const component = useMemo(() => {
    if (!isOpen) return null;

    return (
      <RegisterFormDialog
        {...options}
        provinces={provinces}
        open={isOpen}
        onClose={onSelfCloseModal}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, options, provinces]);

  const api = Object.freeze({
    open: onOpen,
    close: onSelfCloseModal,
    addButtonClick: addRegisterButtonsClick,
    options: options,
  });

  return { api, component } as RegisterApi;
};
